import { CurrencyPipe, DatePipe, registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '@config/sso-auth-config';
import { LOCAL_STORAGE_CONSTANT } from '@constants/localstorage.constant';
import { IPartnerClientId } from '@db-models/partner-db.model';
import { environment } from '@environments/environment';
import { ConfigService } from '@feature-services/config.service';
import { GlobalErrorHandler } from '@interceptors/global-error-handler.interceptor';
import { NoopHttpInterceptor } from '@interceptors/noop-http.interceptor';
import { provideTranslateService, TranslateCompiler, TranslateLoader } from '@ngx-translate/core';
import { BrandTranslateCompiler } from '@shared/compilers/brand-translate-compiler';
import { MergeTranslateLoader } from '@shared/loaders/merge-translate-loader';
import { CalioDateAdapterService } from '@util-services/calio-date-adapter.service';
import { CryptoService } from '@util-services/crypto.service';
import { CalioMatPaginatorIntlService } from '@util-services/mat-paginator.service';
import { DateAdapter as CalendarDateAdapter, provideCalendar } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { provideNgxStripe } from 'ngx-stripe';
import { ToastrModule } from 'ngx-toastr';
import { take } from 'rxjs';

// locals import required for currency pipe and date pipe
import { Overlay } from '@angular/cdk/overlay';
import localeDe from '@angular/common/locales/de';
import localeDeCH from '@angular/common/locales/de-CH';
import localeEs from '@angular/common/locales/es';
import localeFi from '@angular/common/locales/fi';
import localeFr from '@angular/common/locales/fr';
import localeFrCH from '@angular/common/locales/fr-CH';
import localeIt from '@angular/common/locales/it';
import localeItCh from '@angular/common/locales/it-CH';
import localeNl from '@angular/common/locales/nl';
import localePl from '@angular/common/locales/pl';
import localeSv from '@angular/common/locales/sv';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { provideRouter, TitleStrategy, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { appRoutes } from '@app/app.routes';
import { LUCIDE_ICONS } from '@calio-lib/calio-lucide-icon/lucide-icons.config';
import { UtcInterceptor } from '@interceptors/Utc.interceptor';
import { TraceService } from '@sentry/angular';
import { LucideAngularModule } from 'lucide-angular';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { CalensoTitleStrategy } from '@util-services/calenso-title.strategy';

export const getLocalStorage = (): Storage | null => {
  return (typeof window !== 'undefined') ? window.localStorage : null;
};

export const mSALInstanceFactory = (cryptoService: CryptoService): IPublicClientApplication => {
  const ssoPartnerString = localStorage.getItem(LOCAL_STORAGE_CONSTANT.CALENSO_SELECTED_SSO_CLIENT_ID);
  if (ssoPartnerString) {
    const ssoPartnerClient = JSON.parse(cryptoService.decryptValue(ssoPartnerString)) as IPartnerClientId;
    msalConfig.auth.clientId = ssoPartnerClient.client_id;
  }
  return new PublicClientApplication(msalConfig);
};

export const mSALGuardConfigFactory = (): MsalGuardConfiguration => {
  return {
    interactionType: InteractionType.Redirect
  };
};

export const PICK_FORMATS = {
  parse: {
    dateInput: {
      month: 'short',
      year: 'numeric',
      day: 'numeric'
    }
  },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
};

// required for currency pipe and date pipe
registerLocaleData(localeDe, 'de');
registerLocaleData(localeDeCH, 'ch');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeFrCH, 'fr_CH');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeItCh, 'it_CH');
registerLocaleData(localeNl, 'nl');
registerLocaleData(localeFi, 'fi');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeSv, 'sv');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      })
    ),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([NoopHttpInterceptor])
    ),
    provideHttpClient(withInterceptors([UtcInterceptor])),
    provideCharts(withDefaultRegisterables()),
    CurrencyPipe,
    DatePipe,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    importProvidersFrom(
      FormsModule,
      BrowserAnimationsModule,
      ToastrModule.forRoot({
        preventDuplicates: true,
        newestOnTop: true,
        iconClasses: {
          error: 'toast-error',
          success: 'toast-success'
        }
      }),
      MsalModule,
      LucideAngularModule.pick(LUCIDE_ICONS),
    ),
    provideTranslateService({
      loader: { provide: TranslateLoader, useFactory: (http: HttpClient) => new MergeTranslateLoader(http), deps: [HttpClient] },
      compiler: { provide: TranslateCompiler, useClass: BrandTranslateCompiler },
    }),
    provideCalendar({ provide: CalendarDateAdapter, useFactory: adapterFactory }),
    provideNgxStripe(environment.stripe.publishableKey),
    { provide: 'LOCALSTORAGE', useFactory: getLocalStorage },
    { provide: LOCALE_ID, useValue: environment.defaultLocale.replace('_', '-').toLowerCase() },
    { provide: DateAdapter, useClass: CalioDateAdapterService },
    { provide: MAT_DATE_FORMATS, useValue: PICK_FORMATS },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } as RippleGlobalOptions },
    { provide: MSAL_INSTANCE, useFactory: mSALInstanceFactory, deps: [CryptoService] },
    { provide: MSAL_GUARD_CONFIG, useFactory: mSALGuardConfigFactory },
    { provide: MatPaginatorIntl, useClass: CalioMatPaginatorIntlService },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: APP_INITIALIZER, useFactory: () => () => { }, deps: [TraceService], multi: true },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (configService: ConfigService) => {
        return () => {
          configService.getConfigs();
          return configService.configs$.pipe(take(1));
        };
      },
      deps: [ConfigService],
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      // This object is used INSTEAD of `new MatDialogConfig()` (dialog-module: the injected
      // defaults replace the class defaults rather than merging), so spread the real defaults
      // before overriding — otherwise every property left out here is passed down as undefined.
      useFactory: (overlay: Overlay) => ({
        ...new MatDialogConfig(),
        scrollStrategy: overlay.scrollStrategies.noop(),
        // Our dialogs are modal (backdrop + focus trap); the default is false, so assistive
        // technology was never told they are modal.
        ariaModal: true,
      }),
      deps: [Overlay]
    },
    { provide: TitleStrategy, useClass: CalensoTitleStrategy }
  ]
};
